import { api } from '@/lib/api';
import type {
  AddNotePayload,
  ApprovePayload,
  CloseTicketPayload,
  CreateTicketPayload,
  HandoffPayload,
  ListTicketsQuery,
  LogCxPayload,
  PaginatedResponse,
  ChangeCategoryPayload,
  ReassignPayload,
  TicketDetail,
  TicketSummary,
  UpdateStatusPayload,
  UpdateResolutionPayload
} from '@/types/api';

function buildQuery(params: ListTicketsQuery): Record<string, string | number | undefined> {
  const out: Record<string, string | number | undefined> = {};
  if (params.status?.length) out.status = params.status.join(',');
  if (params.category?.length) out.category = params.category.join(',');
  if (params.store?.length) out.store = params.store.join(',');
  if (params.severity?.length) out.severity = params.severity.join(',');
  if (params.breach_flag?.length) out.breach_flag = params.breach_flag.join(',');
  // V4.9.12 — Dashboard drill-down filters.
  if (params.open) out.open = '1';
  if (params.approval_state?.length) out.approval_state = params.approval_state.join(',');
  if (params.owner_id) out.owner_id = params.owner_id;
  if (params.search) out.search = params.search;
  if (params.created_after) out.created_after = params.created_after;
  if (params.created_before) out.created_before = params.created_before;
  if (params.sort) out.sort = params.sort;
  if (params.page) out.page = params.page;
  if (params.per_page) out.per_page = params.per_page;
  return out;
}

export const ticketsApi = {
  list: (query: ListTicketsQuery = {}) =>
    api.get<PaginatedResponse<TicketSummary>>('/tickets', { params: buildQuery(query) }),
  create: (payload: CreateTicketPayload) => api.post<TicketDetail>('/tickets', payload),
  getById: (id: string) => api.get<TicketDetail>(`/tickets/${id}`),
  updateStatus: (id: string, payload: UpdateStatusPayload) =>
    api.patch<TicketDetail>(`/tickets/${id}/status`, payload),
  // V4.9.26 — update the solver "Resolution details" anytime, independent of notes.
  updateResolution: (id: string, payload: UpdateResolutionPayload) =>
    api.patch<TicketDetail>(`/tickets/${id}/resolution`, payload),
  logCx: (id: string, payload: LogCxPayload) =>
    api.post<{ success: true }>(`/tickets/${id}/cx-communications`, payload),
  // V4.9.3 — response may include `auto_submitted_for_approval: true` when
  // the assigned solver's public note auto-flipped the ticket to Waiting
  // Approval. AddNoteModal reads this to show the right confirmation toast.
  // V4.9.5+ — `auto_handed_off_to_communicator: true` for the no-approval
  // path (A5/A6/B*) where solver's reply hands directly to communicator.
  addNote: (id: string, payload: AddNotePayload) =>
    api.post<{
      success: true;
      auto_submitted_for_approval?: boolean;
      auto_handed_off_to_communicator?: boolean;
    }>(`/tickets/${id}/notes`, payload),
  // V4.9.13 — Explicit "Tag teammates" action used by the shared Tag
  // section in every action modal. Tags by user ID so they always land in
  // the recipient's My Tags (no @alias parsing → no multi-word glitch).
  tag: (id: string, payload: { user_ids: string[]; note?: string }) =>
    api.post<{ success: true; tagged: number }>(`/tickets/${id}/tag`, payload),
  close: (id: string, payload: CloseTicketPayload) =>
    api.post<{ success: true; warning: string | null }>(`/tickets/${id}/close`, payload),
  reassign: (id: string, payload: ReassignPayload) =>
    api.post<TicketDetail>(`/tickets/${id}/reassign`, payload),
  handoff: (id: string, payload: HandoffPayload) =>
    api.patch<{ success: true }>(`/tickets/${id}/handoff`, payload),
  changeCategory: (id: string, payload: ChangeCategoryPayload) =>
    api.patch<TicketDetail>(`/tickets/${id}/category`, payload),
  // V4.9.10 — Admin sets fault_attribution on a ticket (sales / shipping /
  // vendor / customer / design / internal_team / none / null). Used by
  // the Reports module's fault tracking + repeat offenders.
  setFault: (id: string, fault: string | null) =>
    api.patch<TicketDetail>(`/tickets/${id}/fault`, { fault }),
  approve: (id: string, payload: ApprovePayload) =>
    api.post<TicketDetail>(`/tickets/${id}/approve`, payload),
  remove: (id: string) => api.delete<{ success: true }>(`/tickets/${id}`),
  archive: (id: string) =>
    api.post<{ success: true }>(`/tickets/${id}/archive`),
  unarchive: (id: string) =>
    api.post<{ success: true }>(`/tickets/${id}/unarchive`),
  restoreDeleted: (id: string) =>
    api.post<{ success: true }>(`/tickets/${id}/restore`),
  listArchived: () => api.get<TicketSummary[]>('/tickets/archived/list'),
  listDeleted: () => api.get<TicketSummary[]>('/tickets/deleted/list')
};
